import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../login/login.scss';
import { KakaoButton } from '../login/KakaoButton';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d).{8,64}$/;

const instruments = ['바이올린', '비올라', '첼로', '더블베이스', '피아노', '성악', '관악', '타악', '작곡·지휘', '기타'];

type Field = 'name' | 'email' | 'password' | 'passwordConfirm' | 'agree';
type Errors = Partial<Record<Field, string>>;

function BrandMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <path d="M3 19c0-6.6 4.5-12 10-12s10 5.4 10 12" stroke="#0B0B0A" strokeWidth="1.3" />
      <path d="M13 7V3" stroke="#0B0B0A" strokeWidth="1.3" />
      <circle cx="13" cy="20.5" r="2.4" fill="#D9902A" />
    </svg>
  );
}

export default function Signup() {

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [kakaoLinked, setKakaoLinked] = useState(false);
  // 카카오에서 이메일을 받아온 경우 수정할 수 없게 고정
  const [kakaoEmailLocked, setKakaoEmailLocked] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [instrument, setInstrument] = useState('');
  const [agree, setAgree] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [statusMsg, setStatusMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 카카오로 처음 들어온 경우(/signup?kakao=1) 카카오 계정의 이메일·이름을 미리 채운다
  useEffect(() => {
    if (searchParams.get('kakao') !== '1') return;
    axios.get('/api/auth/kakao/signup')
      .then((res) => {
        const kakao = res.data.kakao;
        setKakaoLinked(true);
        if (kakao.email) {
          setEmail(kakao.email);
          setKakaoEmailLocked(true);
        }
        if (kakao.name) setName(kakao.name);
      })
      .catch(() => setStatusMsg('카카오 정보를 불러오지 못했습니다. 카카오 버튼을 다시 눌러 주세요.'));
  }, [searchParams]);

  const validate = (): Errors => {
    const next: Errors = {};
    if (!name.trim() || name.trim().length > 30) next.name = '이름을 30자 이내로 입력해 주세요.';
    if (!EMAIL_REGEX.test(email.trim())) next.email = '이메일 주소를 다시 확인해 주세요.';
    // 카카오 가입은 비밀번호 없이 진행
    if (!kakaoLinked) {
      if (!PASSWORD_REGEX.test(password)) next.password = '영문과 숫자를 포함해 8자 이상 입력해 주세요.';
      if (password !== passwordConfirm) next.passwordConfirm = '비밀번호가 일치하지 않습니다.';
    }
    if (!agree) next.agree = '이용약관과 개인정보 처리방침에 동의해 주세요.';
    return next;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    setStatusMsg('');
    if (Object.keys(next).length > 0) return;

    setIsLoading(true);
    try {
      await axios.post('/api/auth/signup', {
        name: name.trim(),
        email: email.trim(),
        ...(kakaoLinked ? {} : { password }),
        instrument,
        agree,
      });
      navigate('/');
    } catch (err) {
      const data = axios.isAxiosError(err) ? err.response?.data : undefined;
      // 카카오 가입에서는 비밀번호 칸이 숨겨져 있으므로 해당 오류는 하단 문구로 보여준다
      if (data?.field && !(kakaoLinked && data.field.startsWith('password'))) {
        setErrors({ [data.field]: data.message });
      } else {
        setStatusMsg(data?.message || '잠시 후 다시 시도해 주세요.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">

      <header className="login-nav">
        <div className="login-nav-in">
          <Link className="brand" to="/">
            <BrandMark size={24} />
            <span className="brand-txt">
              <span className="brand-kr">클래시커</span>
              <span className="brand-lat">CLASSICKER</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="login-main">
        <div className="login-grid">

          {/* ---------------- aside ---------------- */}
          <aside className="login-aside">
            <p className="eyebrow">Join Classicker</p>
            <h1>연주한 모든 곡을<br />한 장의 <em>프로필</em>로.</h1>
            <p className="lede">레퍼토리, 연주 이력, 이번 시즌 일정까지. 가입하고 10분이면 첫 프로필이 채워집니다.</p>
            <div className="staff" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i></div>
          </aside>

          {/* ---------------- form ---------------- */}
          <section className="login-card">
            <div className="card-head">
              <span>Sign up</span>
              <span>회원가입</span>
            </div>

            {kakaoLinked ? (
              <p className="field-hint">카카오 계정 정보를 불러왔습니다. 비밀번호 없이 카카오 로그인으로 이용할 수 있습니다.</p>
            ) : (
              <>
                <KakaoButton label="카카오로 3초 만에 시작하기" />
                <div className="divider"><span>또는 이메일로 가입</span></div>
              </>
            )}

            <form className="login-form" onSubmit={handleSubmit} noValidate>
              <label className="field">
                <span className="field-label">이름</span>
                <input
                  type="text"
                  placeholder="프로필에 표시될 이름"
                  autoComplete="name"
                  maxLength={30}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-invalid={!!errors.name}
                />
                {errors.name && <span className="field-err">{errors.name}</span>}
              </label>

              <label className="field">
                <span className="field-label">이메일</span>
                <input
                  type="email"
                  placeholder="name@example.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  readOnly={kakaoEmailLocked}
                  aria-invalid={!!errors.email}
                />
                {kakaoEmailLocked && <span className="field-hint">카카오 계정의 이메일로 가입되며 수정할 수 없습니다.</span>}
                {errors.email && <span className="field-err">{errors.email}</span>}
              </label>

              {!kakaoLinked && (<>
              <label className="field">
                <span className="field-label">비밀번호</span>
                <span className="pw-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="영문·숫자 포함 8자 이상"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={!!errors.password}
                  />
                  <button
                    type="button"
                    className="pw-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                  >
                    {showPassword ? '숨기기' : '보기'}
                  </button>
                </span>
                {errors.password && <span className="field-err">{errors.password}</span>}
              </label>

              <label className="field">
                <span className="field-label">비밀번호 확인</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="비밀번호를 한 번 더 입력"
                  autoComplete="new-password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  aria-invalid={!!errors.passwordConfirm}
                />
                {errors.passwordConfirm && <span className="field-err">{errors.passwordConfirm}</span>}
              </label>
              </>)}

              <label className="field">
                <span className="field-label">주 악기 · 분야<span className="field-opt">선택</span></span>
                <select value={instrument} onChange={(e) => setInstrument(e.target.value)}>
                  <option value="">선택 안 함</option>
                  {instruments.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </label>

              <div className="agree">
                <label className="check">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                  <span>
                    <a href="/" target="_blank" rel="noreferrer">이용약관</a>과{' '}
                    <a href="/" target="_blank" rel="noreferrer">개인정보 처리방침</a>에 동의합니다. (필수)
                  </span>
                </label>
                {errors.agree && <span className="field-err">{errors.agree}</span>}
              </div>

              <button className="btn btn-primary btn-block" type="submit" disabled={isLoading}>
                {isLoading ? '가입 중…' : '가입하기'}
              </button>
              <p className="status-msg" role="status">{statusMsg}</p>
            </form>

            <div className="card-foot">
              이미 계정이 있으신가요? <Link to="/login">로그인</Link>
            </div>
          </section>

        </div>
      </main>

      <footer className="login-foot">
        <span>© 2026 CLASSICKER</span>
        <span>
          <a href="/">이용약관</a> · <a href="/">개인정보 처리방침</a>
        </span>
      </footer>

    </div>
  );
}
